import { NOT_KNOWN } from 'customer-data-filters/converters/contactSearch/FilterContactSearchOperatorTypes';
import { OWNER_ID } from 'customer-data-objects/task/TaskPropertyNames';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useEffect, useMemo } from 'react';
// @ts-expect-error untyped
import Select from 'react-select-plus';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UICheckbox from 'UIComponents/input/UICheckbox';
import virtualizedMenuRenderer from 'UIComponents/input/utils/virtualizedMenuRenderer';
import { ME_OWNER, NO_OWNER, OPERATORS_TO_NOT_SHOW_CHECKS_IN_SELECT } from '../../constants/viewFilters';
import { useDispatch, useSelector } from '../../context/Redux';
import { useHubSpotUserOwnerIds } from '../../hooks/useHubSpotUserOwnerIds';
import { useTaskViewPermissions } from '../../hooks/useTaskViewPermissions';
import { useTempFilters } from '../../hooks/useTempFilters';
import { updateTempViewCrmSearchQuery } from '../../redux/actions/tempViewState';
import { currentUserAllEmailAddressesSelector, isJitaUserSelector, ownerIdSelector } from '../../redux/selectors/auth';
import { getNoOwnerFilter, getOwnerIdFilter } from '../../utils/crmSearchFilters';
import { getPropertyFilter } from '../../utils/getPropertyFilterValue';
import OwnerSelect, { getDropdownText } from './OwnerSelect';
import QuickFilterContainer from './QuickFilterContainer';
import { tempViewStateSelector } from '../../redux/selectors/tempViewState';
import { jsx as _jsx } from "react/jsx-runtime";
export const __getOwnerOptionFilter = ({
  teammateOwnerIds,
  canViewAllTasks,
  canViewUnassignedTasks,
  canViewTeamTasks,
  currentUserOwnerId,
  value,
  isNoAssigneeOptionSelected
}) => {
  return (options, filterValue, excludeOptions, props) => {
    options = Select.defaultProps.filterOptions(options, filterValue, excludeOptions, props);

    // if user has permission to view unassigned tasks and the option
    // is selected, disable all the other options
    if ((canViewAllTasks || canViewUnassignedTasks) && isNoAssigneeOptionSelected(value)) {
      return options.map(option => {
        return Object.assign({}, option, {
          disabled: true
        });
      });
    }
    if (canViewAllTasks) {
      return options;
    }
    return options.map(option => {
      if (option.value === ME_OWNER) {
        return Object.assign({}, option, {
          disabled: false
        });
      }
      if (teammateOwnerIds.includes(parseInt(option.value, 10))) {
        const disabled = !canViewTeamTasks;
        return Object.assign({}, option, {
          disabled,
          dropdownText: getDropdownText(option, disabled)
        });
      }
      const disabled = option.value !== String(currentUserOwnerId);
      return Object.assign({}, option, {
        disabled,
        dropdownText: getDropdownText(option, disabled)
      });
    });
  };
};
export default function AssigneeFilter({
  viewId
}) {
  const crmSearchFilters = useTempFilters(viewId);
  const dispatch = useDispatch();
  const isJitaUser = useSelector(isJitaUserSelector);
  const currentUserOwnerId = useSelector(ownerIdSelector);
  const viewFromState = useSelector(tempViewStateSelector)[viewId];
  const value = useMemo(() => {
    const filterObject = getPropertyFilter(crmSearchFilters, OWNER_ID);
    if (!filterObject || OPERATORS_TO_NOT_SHOW_CHECKS_IN_SELECT.includes(filterObject.operator)) {
      return [];
    } else if (filterObject.operator === NOT_KNOWN) {
      return [NO_OWNER];
    } else {
      return filterObject.value ? [filterObject.value] : filterObject.values;
    }
  }, [crmSearchFilters]);
  const {
    canViewAllTasks,
    canViewUnassignedTasks,
    canViewOwnedTasksOnly,
    currentUserTeammateIds,
    canViewTeamTasks
  } = useTaskViewPermissions();
  // we only really need teammate ids if we have team permissions but NOT all permissions.
  const teammateOwnerIds = useHubSpotUserOwnerIds(!canViewAllTasks && canViewTeamTasks ? currentUserTeammateIds : []);
  const currentUserPrimaryEmailAddress = useSelector(currentUserAllEmailAddressesSelector)[0];
  const isNoAssigneeOptionSelected = useCallback(selectedOptions => {
    return selectedOptions.length === 1 && selectedOptions[0] === NO_OWNER;
  }, []);
  const handleChange = useCallback(event => {
    const eventValues = event.target.value;
    const filtersWithoutAssignee = crmSearchFilters.filter(filter => filter.property !== OWNER_ID);
    let newFilters;
    if (!Array.isArray(eventValues)) {
      newFilters = filtersWithoutAssignee;
    }
    if (eventValues.length === 0) {
      newFilters = filtersWithoutAssignee;
    } else if (isNoAssigneeOptionSelected(eventValues)) {
      newFilters = [...filtersWithoutAssignee, getNoOwnerFilter()];
    } else {
      newFilters = [...filtersWithoutAssignee, getOwnerIdFilter(eventValues.filter(v => v !== NO_OWNER))];
    }
    dispatch(updateTempViewCrmSearchQuery({
      viewId,
      crmSearchFilters: newFilters
    }));
  }, [viewId, dispatch, crmSearchFilters, isNoAssigneeOptionSelected]);

  // if you don't have access to view all tasks or unassigned tasks then you can get in a weird
  // state where the selected value is disabled. So if this is the case we'll update the default value
  const canOnlyViewOwnTasksAndFilterIsNotMe = !canViewAllTasks && !canViewUnassignedTasks && !canViewTeamTasks && !value.includes(ME_OWNER) && !value.includes(currentUserOwnerId);

  // Update the assignee filter to "ME" if the user lacks permission
  // checking if viewFromState exists in order to wait for the seed view first
  useEffect(() => {
    if (canOnlyViewOwnTasksAndFilterIsNotMe && viewFromState) {
      handleChange(syntheticEvent([ME_OWNER]));
    }
  }, [handleChange, canOnlyViewOwnTasksAndFilterIsNotMe, viewFromState]);
  const numberOfSelectedOptions = value && value.length ? value.length : 0;
  const UnassignedDropdownFooter = useMemo(() => {
    const disabled = canViewAllTasks ? false : !canViewUnassignedTasks;
    return /*#__PURE__*/_jsx(UICheckbox, {
      "data-selenium-test": "quickfilters-unassigned",
      disabled: disabled,
      checked: value.includes(NO_OWNER),
      onChange: event => {
        const isChecked = event.target.checked || false;
        const newValue = isChecked ? [NO_OWNER] : [];
        handleChange(syntheticEvent(newValue));
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "tasks-table.filters.assignedTo.noOwner"
      })
    });
  }, [value, canViewAllTasks, canViewUnassignedTasks, handleChange]);
  const renderButtonContent = useCallback(() => {
    if (numberOfSelectedOptions > 0) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'tasks-table.filters.assignedTo.numSelected',
        options: {
          numSelected: numberOfSelectedOptions
        }
      });
    } else {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'tasks-table.filters.assignedTo.label'
      });
    }
  }, [numberOfSelectedOptions]);
  const filterFunction = useMemo(() => {
    return __getOwnerOptionFilter({
      teammateOwnerIds,
      canViewAllTasks,
      canViewUnassignedTasks,
      canViewTeamTasks,
      currentUserOwnerId,
      value,
      isNoAssigneeOptionSelected
    });
  }, [teammateOwnerIds, canViewAllTasks, canViewUnassignedTasks, canViewTeamTasks, currentUserOwnerId, value, isNoAssigneeOptionSelected]);
  const extraOptions = [{
    text: I18n.text('tasks-table.filters.assignedTo.me'),
    value: ME_OWNER,
    help: isJitaUser ? 'JITA: Dynamically uses the selected user.' : currentUserPrimaryEmailAddress
  }];
  const shouldHighlightFilter = useMemo(() => {
    const filterObject = getPropertyFilter(crmSearchFilters, OWNER_ID);
    const isFilterApplied = filterObject && OPERATORS_TO_NOT_SHOW_CHECKS_IN_SELECT.includes(filterObject.operator);
    const areMultipleAssigneesSelected = Array.isArray(value) && value.length > 0;
    return areMultipleAssigneesSelected || isFilterApplied;
  }, [value, crmSearchFilters]);
  return /*#__PURE__*/_jsx(QuickFilterContainer, {
    filterName: "assignee",
    onClear: () => {
      handleChange(syntheticEvent([]));
    },
    shouldHighlight: shouldHighlightFilter,
    shouldShowClearButton: shouldHighlightFilter,
    children: /*#__PURE__*/_jsx(OwnerSelect, {
      "data-selenium-test": "assignee-quick-filter",
      "data-test-id": "assignee-quick-filter"
      // Filter expects strings as values; convert to string here as owner IDs in saved view definitions may be numbers
      ,
      value: value.map(String),
      options: extraOptions,
      onChange: handleChange,
      filterOptions: filterFunction,
      disabled: canViewOwnedTasksOnly,
      multi: true,
      placement: "bottom",
      buttonUse: "transparent",
      showTags: false,
      clearable: true,
      menuWidth: 300,
      menuRenderer: virtualizedMenuRenderer // TODO: This is part of Select Migration and dependent on other package
      ,
      dropdownFooter: UnassignedDropdownFooter,
      ButtonContent: renderButtonContent
    })
  });
}